import { computed, makeObservable } from 'mobx';

import {
  ChannelCommonServer,
  ChannelKind,
  CreateChannelError,
  CreateWazzupError,
  mapCreateChannelErrorToMessage,
  mapCreateWazzupChannelErrorToMessage
} from 'shared/entities/channels';
import { apiUrls } from 'shared/entities/domain';
import { IRootStore } from 'shared/entities/store/rootStore';
import { validateIsEmpty } from 'shared/entities/validator';
import { WazzupCreationCaseId, WazzupTokenKind } from 'shared/entities/wazzup';
import { CommonChannelModel } from 'shared/models/channel';
import { FieldModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';

export class WazzupCreationStore {
  readonly kind: ChannelKind.WZ = ChannelKind.WZ;
  readonly token: FieldModel<string> = new FieldModel<string>('', [
    validateIsEmpty
  ]);
  readonly tokenKind: FieldModel<WazzupTokenKind | null>;
  readonly creationStage: LoadingStageModel = new LoadingStageModel();
  readonly openedCase: FieldModel<WazzupCreationCaseId | null> = new FieldModel(
    null
  );
  private rootStore: IRootStore;

  constructor({
    rootStore,
    tokenKind
  }: {
    rootStore: IRootStore;
    tokenKind?: WazzupTokenKind;
  }) {
    this.rootStore = rootStore;
    this.tokenKind = new FieldModel(tokenKind ?? null);

    makeObservable(this, {
      isError: computed
    });
  }

  get isError(): boolean {
    return this.token.isError;
  }
  validate(): boolean {
    this.token.validate();

    return this.isError;
  }

  reset(): void {
    this.token.reset();
    this.tokenKind.reset();
  }

  create = async (): Promise<BaseResponse<{ channelId: string }>> => {
    if (this.creationStage.isLoading) {
      return {
        isError: true
      };
    }

    const isError = this.validate();

    if (isError) {
      return {
        isError: true
      };
    }

    this.creationStage.loading();

    const response = await this.rootStore.networkStore.api<
      {
        channel: ChannelCommonServer;
      },
      CreateChannelError | CreateWazzupError
    >(apiUrls.CHANNELS_ADD_WZ, {
      method: 'POST',
      data: {
        token: this.token.value,
        token_kind: this.tokenKind.value
      },
      errorsMap: mapCreateWazzupChannelErrorToMessage
    });

    if (response.isError) {
      this.creationStage.error();

      if (
        response.data?.code &&
        Object.values(CreateChannelError).includes(response.data?.code)
      ) {
        this.token.setError(mapCreateChannelErrorToMessage(response.data.code));
      }

      return {
        isError: true
      };
    }

    const channel = CommonChannelModel.fromJson(
      response.data.channel,
      this.rootStore
    );
    this.rootStore.channelsStore.addCreatedChannel(channel);

    this.reset();
    this.creationStage.success();

    return {
      isError: false,
      data: {
        channelId: channel.id
      }
    };
  };
}
